'use client';

import { Session } from 'next-auth';
import {
    getSession as getNextAuthSession,
    SessionProvider as NextAuthSessionProvider,
    signIn as nextAuthSignIn,
    signOut as nextAuthSignOut,
} from 'next-auth/react';
import { Routes } from '@/app/routes';
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';

interface SessionProviderProps {
    children: ReactNode;
    session: Session | null;
    refetchInterval?: number;
    refetchOnWindowFocus?: boolean;
}

// Create a shared context for session state to prevent multiple fetches
interface SharedSessionContextValue {
    session: Session | null;
    status: 'loading' | 'authenticated' | 'unauthenticated';
    refreshSession: () => Promise<Session | null>;
}

const SharedSessionContext = createContext<SharedSessionContextValue | null>(null);

// Global session cache - accessible outside React components (for axios interceptors)
let globalSessionCache: Session | null = null;

// Set the global session cache (called during provider initialization)
export function setGlobalSession(session: Session | null) {
    globalSessionCache = session;
}

// Get session without triggering API fetch - uses the cached server session
export async function getSession(): Promise<Session | null> {
    // Return the cached session immediately (no API fetch)
    if (globalSessionCache) {
        return globalSessionCache;
    }
    // Only fall back to NextAuth if cache is empty (shouldn't happen in normal flow)
    return getNextAuthSession();
}

/**
 * Fetches a fresh session from the server.
 * This triggers the JWT callback in auth.ts which handles token refresh.
 */
async function fetchFreshSession(): Promise<Session | null> {
    try {
        // Call NextAuth's session endpoint - this triggers the JWT callback server-side
        // which handles token refresh if needed (when timeSinceIssued > 1800)
        const session = await getNextAuthSession();
        return session;
    } catch (error) {
        console.error('Failed to refresh session:', error);
        return null;
    }
}

// Internal provider that manages session state with periodic refresh
function SharedSessionProvider({
    children,
    initialSession,
    refetchInterval = 1800, // 30 minutes
    refetchOnWindowFocus = true,
}: {
    children: ReactNode;
    initialSession: Session | null;
    refetchInterval?: number;
    refetchOnWindowFocus?: boolean;
}) {
    const [session, setSession] = useState<Session | null>(initialSession);
    const [status, setStatus] = useState<'loading' | 'authenticated' | 'unauthenticated'>(
        initialSession ? 'authenticated' : 'unauthenticated',
    );

    // This provider lives in the ROOT layout, which persists (never remounts)
    // across client-side navigations between sibling routes — e.g. /sign-in
    // -> /home after login. `useState(initialSession)` above only seeds state
    // on the FIRST mount; without this effect, a provider that first mounted
    // while unauthenticated (on /sign-in) would stay stuck at
    // session=null/status='unauthenticated' even after the root layout's
    // server component recomputes a fresh, valid `initialSession` on a
    // subsequent navigation.
    useEffect(() => {
        setSession(initialSession);
        setStatus(initialSession ? 'authenticated' : 'unauthenticated');
    }, [initialSession]);

    // Track if a refresh is in progress to prevent concurrent refreshes
    const isRefreshing = useRef(false);
    // Track the last refresh time to implement debouncing
    const lastRefreshTime = useRef<number>(Date.now());

    /**
     * Refreshes the session from the server.
     * This triggers the JWT callback which handles token refresh.
     */
    const refreshSession = useCallback(async (): Promise<Session | null> => {
        // Prevent concurrent refreshes
        if (isRefreshing.current) {
            return session;
        }

        // Debounce: don't refresh if we just refreshed within the last 10 seconds
        const timeSinceLastRefresh = Date.now() - lastRefreshTime.current;
        if (timeSinceLastRefresh < 10000) {
            return session;
        }

        isRefreshing.current = true;
        lastRefreshTime.current = Date.now();

        try {
            const freshSession = await fetchFreshSession();

            if (freshSession) {
                setSession(freshSession);
                setGlobalSession(freshSession);
                setStatus('authenticated');
                return freshSession;
            } else {
                // Session fetch returned null - user may have been logged out
                // or there was an error. Keep existing session if we have one.
                if (!session) {
                    setStatus('unauthenticated');
                }
                return session;
            }
        } finally {
            isRefreshing.current = false;
        }
    }, [session]);

    // Cache the session globally for use outside React (axios interceptors, etc.)
    // Must be a LAYOUT effect, not a passive one: React runs all layout effects
    // in the tree before any passive effect starts, but among passive effects
    // alone, children fire before parents. PrivateLayout (a descendant) fires
    // an unawaited studioService.logUserRouteVisit() from its own plain
    // useEffect on mount, which reads this cache synchronously via the axios
    // request interceptor (src/lib/http/axios/index.ts) to set the
    // Authorization header. With this as a plain useEffect, that child effect
    // could win the race and fire before this one populated the cache,
    // sending the request with no auth header — a guaranteed 401 that the
    // global response interceptor then treated as a real session failure,
    // hard-redirecting straight back to /sign-in right after a successful
    // login. Confirmed via the Network tab: 401 on /users/log-route
    // immediately preceding the bounce.
    useLayoutEffect(() => {
        if (session) {
            setGlobalSession(session);
        }
    }, [session]);

    // Set up periodic session refresh for active users
    // This ensures tokens are refreshed via the JWT callback in auth.ts
    useEffect(() => {
        // Only set up refresh for authenticated users
        if (status !== 'authenticated' || refetchInterval <= 0) {
            return;
        }

        const intervalId = setInterval(() => {
            // Only refresh if the document is visible (user is active)
            if (document.visibilityState === 'visible') {
                refreshSession();
            }
        }, refetchInterval * 1000);

        return () => clearInterval(intervalId);
    }, [status, refetchInterval, refreshSession]);

    // Refresh session when window regains focus (user returns to tab)
    useEffect(() => {
        if (!refetchOnWindowFocus || status !== 'authenticated') {
            return;
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                // User returned to the tab - refresh session
                // The debounce in refreshSession prevents excessive calls
                refreshSession();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [status, refetchOnWindowFocus, refreshSession]);

    const contextValue = useMemo(
        () => ({
            session,
            status,
            refreshSession,
        }),
        [session, status, refreshSession],
    );

    return (
        <SharedSessionContext.Provider value={contextValue}>
            {children}
        </SharedSessionContext.Provider>
    );
}

/**
 * Custom hook that uses the shared session context.
 * Provides access to session data, status, and a manual refresh function.
 */
export function useSession(options?: { required?: boolean; onUnauthenticated?: () => void }) {
    const context = useContext(SharedSessionContext);

    if (!context) {
        throw new Error('useSession must be used within EnhancedSessionProvider');
    }

    const { session, status, refreshSession } = context;

    // Handle required session - redirect to sign-in if unauthenticated
    useEffect(() => {
        if (options?.required && status === 'unauthenticated') {
            if (options.onUnauthenticated) {
                options.onUnauthenticated();
            } else {
                // Default behavior: redirect to this app's OWN custom sign-in page —
                // NOT NextAuth's bare `signIn()`, which sends the browser through
                // NextAuth's generic built-in /api/auth/signin flow (and its
                // `redirect` callback in authOptions) instead. That was confirmed,
                // via live reproduction, to be an independent, real source of the
                // "/undefined/sign-in?callbackUrl=..." redirect corruption seen
                // elsewhere in this app — a completely separate trigger from
                // useSessionError (src/@auth/hooks/useAuth.ts), which already
                // redirects to the correct custom page the same way this does.
                if (
                    typeof window !== 'undefined' &&
                    !window.location.pathname.includes(Routes.SIGNIN)
                ) {
                    const currentUrl = window.location.pathname + window.location.search;
                    window.location.href = `${Routes.SIGNIN}?callbackUrl=${encodeURIComponent(currentUrl)}`;
                }
            }
        }
    }, [options?.required, options?.onUnauthenticated, status]);

    return {
        data: session,
        status,
        /**
         * Manually trigger a session refresh.
         * Useful after operations that might have changed the user's permissions.
         */
        refreshSession,
    };
}

/**
 * Enhanced SessionProvider that manages session state with automatic refresh.
 *
 * Features:
 * - Uses initial server session to avoid hydration mismatches
 * - Periodically refreshes session to keep tokens valid (default: every 5 minutes)
 * - Refreshes session when user returns to the tab
 * - Provides manual refresh function via useSession hook
 *
 * The periodic refresh triggers the JWT callback in auth.ts which handles
 * token refresh when timeSinceIssued > 1800 (30 minutes).
 */
export function EnhancedSessionProvider({
    children,
    session,
    refetchInterval = 300, // 5 minutes - aligns with profile cache expiry
    refetchOnWindowFocus = true,
}: SessionProviderProps) {
    return (
        <SharedSessionProvider
            initialSession={session}
            refetchInterval={refetchInterval}
            refetchOnWindowFocus={refetchOnWindowFocus}
        >
            {children}
        </SharedSessionProvider>
    );
}

// Re-export NextAuth functions for use outside React components
// Note: getSession is now our custom implementation that uses the cache
export const signIn = nextAuthSignIn;
export const signOut = nextAuthSignOut;

// Re-export SessionProvider for testing and backwards compatibility
export const SessionProvider = NextAuthSessionProvider;
